/**
 * Icon Registry —— 集中维护图标 name → SVG markup 映射。
 *
 * - 数据源走 Vite `import.meta.glob` 的 `?raw` 形式 + `eager: true`，
 *   构建期就把 `src/assets/icons/*.svg` 全部内联为字符串，
 *   运行时不触发 HTTP 请求。
 * - `IconName` 联合类型由 `ICON_NAMES` 显式声明，registry 启动期校验
 *   每个合法 name 都有对应 SVG；缺 SVG 直接抛错，暴露配置错误。
 * - 未注册 name 的处理由 `<Icon>` 组件统一兜底（console.warn + 占位），
 *   注册表本身不做静默 fallback。
 */

export const ICON_NAMES = [
  'close',
  'download',
  'error',
  'impact',
  'info',
  'innovation',
  'link',
  'method',
  'problem',
  'reset',
  'retry',
  'rotate-left',
  'rotate-right',
  'video-play',
  'zoom-in',
  'zoom-out',
] as const;

export type IconName = (typeof ICON_NAMES)[number];

const modules = import.meta.glob<string>(
  '../assets/icons/*.svg',
  { query: '?raw', import: 'default', eager: true },
);

function loadIcon(name: IconName): string {
  const key = `../assets/icons/${name}.svg`;
  const markup = modules[key];
  if (!markup) {
    throw new Error(`[IconRegistry] missing SVG asset for "${name}"`);
  }
  return markup;
}

const registry = Object.fromEntries(
  ICON_NAMES.map((name) => [name, loadIcon(name)]),
) as Readonly<Record<IconName, string>>;

export const iconRegistry: Readonly<Record<IconName, string>> = Object.freeze(
  registry,
);

export function isRegisteredIcon(name: string): name is IconName {
  return Object.prototype.hasOwnProperty.call(iconRegistry, name);
}

/**
 * 首页 grid 区标题图标映射，F12 `resolveGroupTitle` 时由消费方组合：
 * `<Icon :name="sectionGroupIcons[type]" />`。
 *
 * 此映射只声明 type→icon 关系，icon name 必须存在于 `iconRegistry`。
 * 文件 `assets/icons/{problem,method,innovation,impact}.svg` 缺失时启动期抛错。
 */
export const sectionGroupIcons: Readonly<Record<string, IconName>> = {
  problem: 'problem',
  method: 'method',
  innovation: 'innovation',
  impact: 'impact',
};
